import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchCount, useFetchResource } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import useAssessmentQuestionnaireLink from '@app/src/hooks/useAssessmentQuestionnaireLink'
import useOrganizationRecommendations from '@app/src/hooks/useOrganizationRecommendations'
import useProviderType from '@app/src/hooks/useProviderType'
import StatisticsCard from '@app/src/pages/Dashboards/StatisticsCard'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import {
  AssessmentType,
  ProgressTracker,
  ProviderCoverage,
  RecommendationTypeName,
} from '@app/src/types/resourceExplorer'
import { EditOutlined, KeyboardArrowDown } from '@mui/icons-material'
import { Alert, Box, Button, Collapse, Divider, Grid, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import BaselineAssessmentDistributionGraph from './BaselineAssessmentDistributionGraph'
import CreateProgressTrackerModal from './CreateProgressTrackerModal'
import InScopeCoverageGraph from './InScopeCoverageGraph'
import OrganizationRecommendationCard from './OrganizationRecommendationCard'
import ProgressTrackerModalContextProvider from './ProgressTrackerModalContextProvider'
import ProviderCoverageGraph from './ProviderCoverageGraph'
import TargetElement from './TargetElement'

const getPeriodsWithOffset = (offset: number) => {
  const currentYear = new Date().getFullYear()
  if (offset === -1) return `${currentYear - 1} - ${currentYear}`
  if (offset === -2) return `${currentYear - 2} - ${currentYear}`
  return currentYear
}

const getPeriodNamesArray = (trackingPeriodOffset: number): string[] => {
  const currentYear = new Date().getFullYear()
  const periodNames: string[] = []

  periodNames.push(currentYear.toString())

  for (let i = 1; i <= Math.abs(trackingPeriodOffset); i++) {
    periodNames.unshift((currentYear - i).toString())
  }

  return periodNames
}

export const GOAL_TRACKER_DENSE_AMOUNT = 4
export const GOAL_TRACKER_LIMIT = 20

const TrackersSection = () => {
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const isSourcingUser = hasPermission(Permissions.SOURCING_USER)
  const { progressTracker: progressTrackerFlag } = useFlags()
  const providerType = useProviderType()
  const [isCreateProgressTrackerDialogOpen, openCreateProgressTrackerDialog, closeCreateProgressTrackerDialog] =
    useDialogState(false)
  const [isExpanded, setIsExpanded] = useState(true)

  const { assessmentQuestionnaireLinks, isLoadingAssessmentQuestionnaireLink } = useAssessmentQuestionnaireLink({
    assessmentType: AssessmentType.BaselineAssessment,
    includeAssessment: true,
    enabled: isSourcingUser,
  })

  const baselineAssessmentQuestionnaireTemplateId = assessmentQuestionnaireLinks?.find(
    link => link.assessmentTemplate?.assessmentType === AssessmentType.BaselineAssessment,
  )?.questionnaireTemplateId

  const baselineAssessmentTemplate = assessmentQuestionnaireLinks?.find(
    link => link.assessmentTemplate?.assessmentType === AssessmentType.BaselineAssessment,
  )?.assessmentTemplate

  const {
    items: progressTrackers,
    isLoading: isProgressTrackersLoading,
    isError: isProgressTrackersError,
  } = useFetchCollectionWithPost<ProgressTracker>({
    key: FetchKey.ProgressTracker,
    endpoint: endpoints.progressTrackerCollection,
    shouldShowErrorNotification: false,
    payload: {
      filter: [],
      include: [],
    },
    options: { enabled: isSourcingUser && progressTrackerFlag },
  })

  const { count: baselineAssessmentResponsesCount, isLoading: isLoadingBaselineAssessmentResponsesCount } =
    useFetchCount({
      key: FetchKey.ResponseFacets,
      endpoint: endpoints.responsesCount,
      payload: [
        {
          name: 'request.template.id',
          filters: [
            {
              operator: Operators.EqualTo,
              value: baselineAssessmentQuestionnaireTemplateId,
            },
          ],
        },
      ],
      options: {
        enabled: Boolean(baselineAssessmentQuestionnaireTemplateId) && isSourcingUser && progressTrackerFlag,
        staleTime: 60000,
      },
    })

  const baselineAssessmentProgressTracker = progressTrackers?.find(
    tracker => tracker.questionnaireTemplateId === Number(baselineAssessmentQuestionnaireTemplateId),
  )

  const {
    data: providerCoverage,
    isLoading: isLoadingProviderCoverage,
    isError: isProviderCoverageError,
  } = useFetchResource<ProviderCoverage>({
    key: [FetchKey.ProviderCoverage, Number(baselineAssessmentProgressTracker?.id)],
    endpoint: endpoints.getProviderCoverageStatistics(baselineAssessmentProgressTracker?.id),
    options: { enabled: Boolean(baselineAssessmentProgressTracker) && isSourcingUser },
  })

  const {
    data: organizationRecommendations,
    isLoading: isLoadingRecommendations,
    isError: isRecommendationsError,
  } = useOrganizationRecommendations({
    progressTrackerId: baselineAssessmentProgressTracker?.id,
    recommendationTypeNames: [
      RecommendationTypeName.AiScrapedProvidersWithLowAssessmentResult,
      RecommendationTypeName.AiScrapedProvidersWithNoPublicData,
      RecommendationTypeName.NonAiScrapedProvidersWithoutBaselineAssessmentRequest,
    ],
  })

  const activeRecommendations = organizationRecommendations?.filter(
    recommendation => !recommendation.isSnoozed && !recommendation.isCompleted,
  )

  const hasRecommendations = Boolean(activeRecommendations?.length)

  if (!baselineAssessmentResponsesCount || !isSourcingUser || !progressTrackerFlag) {
    return null
  }

  return (
    <ProgressTrackerModalContextProvider>
      <CreationModalProgressContextProvider>
        <Box>
          <StatisticsCard
            title={formatMessage({ id: 'schemas.progressTracker.title' })}
            isError={isProgressTrackersError || isProviderCoverageError || isRecommendationsError}
            action={
              <IconButton onClick={() => setIsExpanded(!isExpanded)}>
                <KeyboardArrowDown
                  sx={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                />
              </IconButton>
            }
            loading={{
              isLoading:
                isLoadingBaselineAssessmentResponsesCount ||
                isLoadingProviderCoverage ||
                isProgressTrackersLoading ||
                isLoadingAssessmentQuestionnaireLink ||
                isLoadingRecommendations,
              skeleton: (
                <Grid container spacing={2}>
                  <Grid item xs={12} mt={1}>
                    <Skeleton height={20} width={150} variant="rounded" />
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton height={50} width={350} variant="rounded" />
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton height={250} variant="rounded" />
                  </Grid>
                </Grid>
              ),
            }}
          >
            <Collapse in={isExpanded} sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Divider sx={{ pt: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack spacing={1}>
                      <Typography variant="h5">
                        {formatMessage({ id: 'baselineAssessment.baselineAssessment' })}
                      </Typography>
                      {baselineAssessmentProgressTracker && (
                        <Typography variant="body1" color="text.secondary">
                          {formatMessage(
                            { id: 'schemas.progressTracker.subHeader' },
                            {
                              providerType,
                              scopeTargetPeriodName: getPeriodsWithOffset(
                                baselineAssessmentProgressTracker.trackingPeriodOffset,
                              ),
                            },
                          )}
                        </Typography>
                      )}
                    </Stack>
                    {baselineAssessmentProgressTracker && (
                      <Button startIcon={<EditOutlined />} onClick={openCreateProgressTrackerDialog}>
                        {formatMessage({ id: 'general.edit' })}
                      </Button>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  {isProgressTrackersLoading || isLoadingAssessmentQuestionnaireLink ? (
                    <Skeleton height={60} />
                  ) : (
                    !baselineAssessmentProgressTracker && (
                      <Alert
                        severity="warning"
                        action={
                          <Button onClick={openCreateProgressTrackerDialog}>
                            {formatMessage({ id: 'schemas.progressTracker.updateNow' })}
                          </Button>
                        }
                      >
                        {formatMessage({ id: 'schemas.progressTracker.callToAction' })}
                      </Alert>
                    )
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={hasRecommendations ? 9 : 12}>
                      <Grid container spacing={4} height="100%">
                        <Grid item xs={12} md={5} lg={2}>
                          <TargetElement progressTracker={baselineAssessmentProgressTracker} />
                        </Grid>
                        <Grid item xs={12} md={hasRecommendations ? 6 : 7} lg={hasRecommendations ? 4 : 5}>
                          <Stack direction="row" height="100%" width="100%">
                            <Box height="100%" pr={4} sx={{ display: { xs: 'none', md: 'flex' } }}>
                              <Divider orientation="vertical" flexItem />
                            </Box>
                            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
                              <Stack spacing={2} width="100%">
                                <InScopeCoverageGraph
                                  providerCoverageData={providerCoverage}
                                  progressTrackerId={baselineAssessmentProgressTracker?.id}
                                  assessmentTemplate={baselineAssessmentTemplate}
                                  periodNames={
                                    baselineAssessmentProgressTracker
                                      ? getPeriodNamesArray(baselineAssessmentProgressTracker.trackingPeriodOffset)
                                      : []
                                  }
                                />
                                <ProviderCoverageGraph
                                  providerCoverageData={providerCoverage}
                                  progressTrackerId={baselineAssessmentProgressTracker?.id}
                                  assessmentTemplate={baselineAssessmentTemplate}
                                  periodNames={
                                    baselineAssessmentProgressTracker
                                      ? getPeriodNamesArray(baselineAssessmentProgressTracker.trackingPeriodOffset)
                                      : []
                                  }
                                />
                              </Stack>
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={hasRecommendations ? 11 : 12} lg={hasRecommendations ? 4 : 5}>
                          <Stack height="100%" width="100%" justifyContent="center">
                            <Typography variant="subtitle1">
                              {formatMessage({ id: 'schemas.progressTracker.distribution' })}
                            </Typography>
                            <BaselineAssessmentDistributionGraph
                              assessmentLevelResults={providerCoverage?.assessmentLevelResults}
                              progressTrackerId={baselineAssessmentProgressTracker?.id}
                              assessmentTemplate={baselineAssessmentTemplate}
                              periodNames={
                                baselineAssessmentProgressTracker
                                  ? getPeriodNamesArray(baselineAssessmentProgressTracker.trackingPeriodOffset)
                                  : []
                              }
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>
                    {hasRecommendations && (
                      <Grid item xs={12} md={3}>
                        <OrganizationRecommendationCard
                          recommendations={activeRecommendations}
                          baselineAssessmentQuestionnaireTemplateId={baselineAssessmentQuestionnaireTemplateId}
                          progressTrackerPeriod={baselineAssessmentProgressTracker?.scopeTargetPeriodName}
                          progressTrackerGoal={baselineAssessmentProgressTracker?.totalCompaniesInScope}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <CreateProgressTrackerModal
                  open={isCreateProgressTrackerDialogOpen}
                  onClose={closeCreateProgressTrackerDialog}
                  questionnaireTemplateId={Number(baselineAssessmentQuestionnaireTemplateId)}
                  progressTrackerItem={baselineAssessmentProgressTracker}
                />
              </Grid>
            </Collapse>
          </StatisticsCard>
          <Stack pt={2} width="100%">
            <Divider flexItem />
          </Stack>
        </Box>
      </CreationModalProgressContextProvider>
    </ProgressTrackerModalContextProvider>
  )
}

export default TrackersSection
